import { useEffect, useState } from "react";
import { Shield, X, Check } from "lucide-react";
import { Lang } from "../i18n";
import { writeSharedConsentCookies } from "../lib/consentCookies";

type Props = {
  lang: Lang;
};

type Preferences = {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
};

const copy = {
  de: {
    headline: "Bevor Sie starten",
    body:
      "Wir und ausgewählte Drittanbieter nutzen Cookies, um NeoSearch zu verbessern, die Nutzung zu analysieren und Werbung zu messen. Die Einwilligung umfasst die Übermittlung von Daten in Drittländer wie die USA (Art. 49 (1) (a) DSGVO) und ist jederzeit widerrufbar.",
    privacy: "Datenschutz",
    imprint: "Impressum",
    providers: "Anbieterliste",
    settings: "Einstellungen",
    rejectAll: "Alle ablehnen",
    acceptAll: "Alle akzeptieren",
    modalTitle: "Cookie-Einstellungen",
    modalLead:
      "Sie haben die Kontrolle. Wählen Sie selbst, welche Kategorien Sie zulassen.",
    necessaryTitle: "Notwendig",
    necessaryTag: "Immer aktiv",
    necessaryDesc:
      "Für die grundlegende Funktion der Website. Diese Cookies können nicht deaktiviert werden.",
    analyticsTitle: "Statistik",
    analyticsDesc:
      "Helfen uns zu verstehen, wie Besucher NeoSearch nutzen – vollständig anonymisiert.",
    marketingTitle: "Marketing",
    marketingDesc:
      "Ermöglichen relevante Werbung und messen, ob unsere Kampagnen funktionieren.",
    cancel: "Zurück",
    save: "Auswahl speichern",
  },
  en: {
    headline: "Before you continue",
    body:
      "We and selected third parties use cookies to improve NeoSearch, analyse usage and measure advertising. Consent includes transferring data to third countries such as the USA (Art. 49 (1) (a) GDPR) and can be withdrawn at any time.",
    privacy: "Privacy",
    imprint: "Imprint",
    providers: "Provider list",
    settings: "Settings",
    rejectAll: "Reject all",
    acceptAll: "Accept all",
    modalTitle: "Cookie settings",
    modalLead: "You're in control. Choose which categories to allow.",
    necessaryTitle: "Necessary",
    necessaryTag: "Always on",
    necessaryDesc:
      "Required for the basic functioning of the site. These cookies cannot be disabled.",
    analyticsTitle: "Statistics",
    analyticsDesc:
      "Help us understand how visitors use NeoSearch – fully anonymised.",
    marketingTitle: "Marketing",
    marketingDesc:
      "Enable relevant advertising and let us measure whether our campaigns work.",
    cancel: "Back",
    save: "Save selection",
  },
} as const;

const persist = (prefs: Preferences) => {
  const now = Date.now().toString();
  localStorage.setItem("cookie-consent", "accepted");
  localStorage.setItem("cookie-consent-date", now);
  localStorage.setItem("cookie-preferences", JSON.stringify(prefs));
  // Hand the decision to the app subdomain immediately (cross-domain cookies).
  writeSharedConsentCookies(prefs);
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("cookie-consent-changed", { detail: prefs })
    );
  }
};

const CookieConsentBlocking = ({ lang }: Props) => {
  const [show, setShow] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const t = copy[lang];

  useEffect(() => {
    // During build-time prerendering there is no consent in localStorage, so
    // the modal would open and bake `body{overflow:hidden}` + the open sheet
    // into the static HTML — permanently locking scroll for anyone who loads
    // the prerendered page already consented. Never show it while prerendering.
    if ((window as { __PRERENDER__?: boolean }).__PRERENDER__) return;

    const consent = localStorage.getItem("cookie-consent");
    const consentDate = localStorage.getItem("cookie-consent-date");

    if (!consent) {
      setShow(true);
      return;
    }
    if (consentDate) {
      const age = Date.now() - parseInt(consentDate);
      const twelveMonths = 12 * 30 * 24 * 60 * 60 * 1000;
      if (age > twelveMonths) {
        localStorage.removeItem("cookie-consent");
        localStorage.removeItem("cookie-consent-date");
        localStorage.removeItem("cookie-preferences");
        setShow(true);
      }
    }
  }, []);

  useEffect(() => {
    if (!show) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [show]);

  const finish = (prefs: Preferences) => {
    persist(prefs);
    setShow(false);
    setShowDetails(false);
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  };

  const acceptAll = () =>
    finish({ necessary: true, analytics: true, marketing: true });
  const rejectAll = () =>
    finish({ necessary: true, analytics: false, marketing: false });

  if (!show) return null;

  if (showDetails) {
    return (
      <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-slate-950/40 sm:p-4">
        <div className="relative bg-white text-slate-900 w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl shadow-2xl flex flex-col max-h-[92vh] sm:max-h-[88vh] overflow-hidden">
          <div className="px-6 pt-6 pb-4 border-b border-slate-100">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold tracking-tight">
                  {t.modalTitle}
                </h3>
                <p className="text-sm text-slate-500 mt-1">{t.modalLead}</p>
              </div>
              <button
                onClick={() => setShowDetails(false)}
                className="-mt-1 p-1.5 text-slate-400 hover:text-slate-700 rounded-full hover:bg-slate-100 transition-colors"
                aria-label={t.cancel}
              >
                <X size={18} />
              </button>
            </div>
          </div>

          <div className="px-6 py-5 space-y-3 overflow-y-auto flex-1">
            <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-slate-900">
                      {t.necessaryTitle}
                    </h4>
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                      {t.necessaryTag}
                    </span>
                  </div>
                  <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">
                    {t.necessaryDesc}
                  </p>
                </div>
                <div className="w-10 h-6 rounded-full bg-emerald-500 flex items-center justify-end px-0.5 shrink-0 mt-1">
                  <div className="w-5 h-5 rounded-full bg-white shadow flex items-center justify-center">
                    <Check size={12} className="text-emerald-600" />
                  </div>
                </div>
              </div>
            </div>

            <ToggleRow
              title={t.analyticsTitle}
              desc={t.analyticsDesc}
              checked={analytics}
              onChange={setAnalytics}
            />
            <ToggleRow
              title={t.marketingTitle}
              desc={t.marketingDesc}
              checked={marketing}
              onChange={setMarketing}
            />
          </div>

          <div className="px-6 py-4 border-t border-slate-100 bg-white flex flex-col-reverse sm:flex-row gap-3">
            <button
              onClick={() => setShowDetails(false)}
              className="flex-1 px-4 py-3 text-sm font-medium border border-slate-300 rounded-xl text-slate-700 hover:bg-slate-50 transition-colors"
            >
              {t.cancel}
            </button>
            <button
              onClick={() =>
                finish({ necessary: true, analytics, marketing })
              }
              className="flex-1 px-4 py-3 text-sm font-semibold bg-slate-900 text-white rounded-xl hover:bg-slate-800 transition-colors"
            >
              {t.save}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Light, blur-free scrim keeps the page headline readable behind the sheet.
  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-slate-950/25 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="consent-headline"
    >
      <div className="bg-white text-slate-900 w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl shadow-2xl border border-slate-200 px-5 pt-4 pb-5 sm:px-6 max-h-[45vh] overflow-y-auto">
        <div className="flex items-center gap-2">
          <Shield size={14} className="text-orange-600 shrink-0" />
          <h2
            id="consent-headline"
            className="text-[15px] font-semibold tracking-tight"
          >
            {t.headline}
          </h2>
        </div>
        <p className="mt-1.5 text-[12.5px] leading-relaxed text-slate-600">
          {t.body}
        </p>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500">
          <button
            onClick={() => setShowDetails(true)}
            className="font-medium text-slate-700 underline underline-offset-2 hover:text-slate-900"
          >
            {t.settings}
          </button>
          <a href="/datenschutz" className="hover:text-slate-800 hover:underline underline-offset-2">
            {t.privacy}
          </a>
          <a href="/impressum" className="hover:text-slate-800 hover:underline underline-offset-2">
            {t.imprint}
          </a>
          <a href="/cookie-policy" className="hover:text-slate-800 hover:underline underline-offset-2">
            {t.providers}
          </a>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2.5">
          <button
            onClick={rejectAll}
            className="px-3 py-2.5 text-sm font-semibold bg-slate-900 text-white rounded-xl hover:bg-slate-800 transition-colors"
          >
            {t.rejectAll}
          </button>
          <button
            onClick={acceptAll}
            className="px-3 py-2.5 text-sm font-semibold bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-colors"
          >
            {t.acceptAll}
          </button>
        </div>
      </div>
    </div>
  );
};

type ToggleRowProps = {
  title: string;
  desc: string;
  checked: boolean;
  onChange: (next: boolean) => void;
};

const ToggleRow = ({ title, desc, checked, onChange }: ToggleRowProps) => (
  <div className="rounded-2xl border border-slate-200 p-4">
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1">
        <h4 className="font-medium text-slate-900">{title}</h4>
        <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{desc}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`shrink-0 mt-1 w-10 h-6 rounded-full transition-colors flex items-center px-0.5 ${
          checked ? "bg-orange-500 justify-end" : "bg-slate-300 justify-start"
        }`}
      >
        <div className="w-5 h-5 rounded-full bg-white shadow" />
      </button>
    </div>
  </div>
);

export default CookieConsentBlocking;
